import React from "react";
import { AppDispatch } from "@redux/hooks";
import { productsApi } from "@redux/features/products/productsApi";
import _ from "lodash";
import { BadgeStatus, SaleOrder } from "@/types/general";
import { SaleOrdersFormTyping } from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/types";
import BaseTooltip from "@reusables/BaseTooltip";
import BaseTinyActionIcon, {
    BaseTinyActionIconProps,
    TinyActionIconType,
} from "@reusables/BaseTinyActionIcon";
import { TFunction } from "react-i18next";
import { Actions, Modules, useAbility } from "@/casl.config";

export interface FlattenedLine {
    data: SaleOrder.Components.LineData;
}

export function flattenLines(lines: SaleOrder.Components.Lines): FlattenedLine[] {
    return lines.flatMap((line) => {
        if (line.type === "line") {
            return [{ data: line.data }];
        } else if (line.type === "group") {
            return line.data?.lines;
        }
        return [];
    });
}

function normalizeTheirReferenceName(value?: string | null): string | undefined {
    const normalizedValue = value?.trim();
    return normalizedValue ? normalizedValue : undefined;
}

export function buildSaleOrderTheirReferencePayload(
    data: Pick<SaleOrdersFormTyping, "their_reference" | "their_reference_name">
): Partial<Pick<SaleOrder.DTO.Create, "their_reference_id" | "their_reference_name">> {
    if (data.their_reference) {
        return {
            their_reference_id: data.their_reference.id,
        };
    }

    const theirReferenceName = normalizeTheirReferenceName(data.their_reference_name);

    return theirReferenceName ? { their_reference_name: theirReferenceName } : {};
}

/**
 * This function is used to load Root products info. One of its main purposes is to extract the purchasing price to further pass it to the PO creation page.
 *
 * @param items - An array of objects, each containing the product_id, quantity_to_order, and an optional discount.
 * @param dispatch - A dispatch function from Redux.
 *
 * @returns A Promise that resolves to an array of objects. Each object contains detailed information about a product, including its id, name, code, quantity to order, unit price, and tax.
 *
 * @throws Will throw an error if the API call fails or if the payload from the API response is not found.
 */
export async function formPurchaseOrderLines(
    items: {
        product_id: number;
        quantity_to_order: number;
        discount?: number;
    }[],
    dispatch: AppDispatch
) {
    const productIds = items.map((item) => item.product_id);
    const itemsKeyedByProductId = _.keyBy(items, "product_id");

    let extendedProductsQuery;
    try {
        // Getting extended product info
        extendedProductsQuery = dispatch(
            productsApi.endpoints.getProductsFull.initiate({
                filters: {
                    ids: productIds,
                },
                reactive: true,
            })
        );

        const extendedProductsData = await extendedProductsQuery.unwrap();

        if (extendedProductsData.payload) {
            return extendedProductsData.payload.map((product) => {
                const quantityToOrder = itemsKeyedByProductId[product.id].quantity_to_order;

                return {
                    product: {
                        id: product.id,
                        name: product.name,
                        code: product.code,
                    },
                    quantity: quantityToOrder,
                    unit_price: product.prices.purchase_price,
                    tax: product.tax,
                };
            });
        } else {
            throw new Error("No payload found");
        }
    } catch (e) {
        console.error(e);

        extendedProductsQuery?.unsubscribe();

        return Promise.reject(e);
    }
}

/**
 * Return a filtered (ones, that need to be purchased) and transformed (quantity is set to the needed number) array of order lines to be used in the refill modal.
 **/
export function formRefillModalLines(lines: SaleOrder.Root["lines"]) {
    const flattenedLines = flattenLines(lines);

    // Process using lodash chain:
    // 1. Filter out service products.
    // 2. Group remaining lines by product ID.
    // 3. For each group, sum ordered quantities, subtract the in-stock amount, and return only if shortage > 0.
    const refillModalLines = _.chain(flattenedLines)
        .filter((line) => !line.data.product.is_service)
        .groupBy((line) => line.data.product.id)
        .map((group) => {
            // All lines in the group are for the same product
            const product = group[0].data.product;
            const totalQuantity = _.sumBy(group, (line) => line.data.quantity);
            const inStock = product.in_stock ?? 0;
            const shortage = totalQuantity - inStock;

            return shortage > 0
                ? {
                      product: {
                          id: product.id,
                          name: product.name,
                          code: product.code,
                          has_bom: product.has_bom,
                      },
                      quantity: shortage,
                  }
                : null;
        })
        .compact()
        .value();

    return refillModalLines;
}

interface EntityWithStatus {
    status: {
        name: BadgeStatus.Base;
    };
    is_invoiced?: boolean;
}

export function isOrderDisabled(actionEntity?: EntityWithStatus): boolean {
    return (
        actionEntity?.status.name === BadgeStatus.Base.cancelled ||
        actionEntity?.status.name === BadgeStatus.Base.completed
    );
}

export function isPrepaidAwaitingInvoice(actionEntity?: EntityWithStatus): boolean {
    return !!actionEntity && actionEntity.status.name === BadgeStatus.Base.prepaid && !actionEntity.is_invoiced;
}

export function isOrderPickingDisabled(actionEntity?: EntityWithStatus): boolean {
    return (
        actionEntity?.status.name === BadgeStatus.Base.offer ||
        isPrepaidAwaitingInvoice(actionEntity) ||
        isOrderDisabled(actionEntity)
    );
}

// This function is used to get the title for the cancel button tooltip
// Provided to avoid ternary operator complexity in the CancelOrderAction component
const getTooltipTitle = (t: TFunction, statusName?: string): string => {
    const statusMessages: Record<string, string> = {
        [BadgeStatus.Base.cancelled]: "alreadyCancelled",
        [BadgeStatus.Base.completed]: "alreadyCompleted",
        // Add more statuses here as needed
    };

    if (statusName && statusMessages[statusName]) {
        return t(`actions.cancellationNotAllowed.${statusMessages[statusName]}`);
    }

    return t("actions.cancel");
};

interface CancelOrderActionProps {
    actionEntity?: EntityWithStatus;
    action: Actions;
    module: Modules;
    t: TFunction;
    onClick: () => void;
    tinyActionIconVariant?: BaseTinyActionIconProps["variant"];
}

export function CancelOrderAction({
    actionEntity,
    action,
    module,
    t,
    onClick,
    tinyActionIconVariant = "gradient",
}: CancelOrderActionProps) {
    const ability = useAbility();

    const isDisabled = isOrderDisabled(actionEntity);

    return (
        <>
            <BaseTooltip active={!isDisabled} title={t("actions.cancel") || ""} placement="bottom">
                <BaseTooltip
                    active={isDisabled}
                    title={getTooltipTitle(t, actionEntity?.status.name)}
                    placement="bottom"
                >
                    <BaseTinyActionIcon
                        type={TinyActionIconType.Cancel}
                        onClick={() => {
                            if (ability.can(action, module) && !isDisabled) {
                                onClick();
                            }
                        }}
                        disabled={isDisabled}
                        variant={tinyActionIconVariant}
                        className={!isDisabled ? "" : "opacity-20 cursor-not-allowed"}
                    />
                </BaseTooltip>
            </BaseTooltip>
        </>
    );
}
